import { buildAi, buildDocInfo, buildHistoryCar, buildNotices, buildStatus, buildTiles, buildVehicle } from './header';
import { buildHistoryItems, buildHistoryNumbers, buildZones } from './history';
import { buildJourney, buildVisits } from './journey';
import { buildMileageChart, buildYears } from './charts';
import { buildChips, buildCondition, buildHero, buildPrice, buildServiceSummary, buildSpecs } from './layout';
import {
    buildEmpty,
    buildEquipment,
    buildInspection,
    buildOriginal,
    buildOutcome,
    buildRepairs,
    buildWaiting,
} from './sections';

export const INITIAL_STATE = { open: {}, allVisits: false, allCodes: false, allRepairs: false, zone: null, year: null };

/**
 * Everything the report page shows, from a report view model (see adapters/) and the page's own state
 * (open visits, selected car area and year, "show all" toggles). A section whose data is missing comes back
 * null or empty and is not rendered: the page never shows a value the documents do not hold.
 * `columns` adds what the two-column layout shows (header, specs cards, condition, price, equipment chips).
 */
export const buildReportView = (report, state, t, { columns = false } = {}) => {
    const r = report || {};
    const s = { ...INITIAL_STATE, ...state };

    const visits = buildVisits(r.timeline, s.open, t);
    const zones = buildZones(
        buildHistoryItems(r.history, t),
        visits.map(v => v.raw),
        s.zone,
        t
    );
    const selectedZone = zones.find(z => z.on) || null;
    const zoneVisits = selectedZone && selectedZone.visitsIn;
    const repairs = buildRepairs(r.repairs, s, t);
    const repairRows = (r.repairs && r.repairs.rows) || [];
    const yearEvents = visits.length
        ? visits.map(v => ({ date: v.raw.date, sortDate: v.raw.sortDate, km: v.raw.km, flag: !!v.raw.flag }))
        : repairRows.map(row => ({ date: row.date, sortDate: row.sortDate, km: row.km, flag: false }));
    const years = buildYears(yearEvents, { selectedYear: s.year, canFilter: visits.length > 0 }, t);

    const mileage = buildMileageChart(r.mileage && r.mileage.points, t, { pointLabels: columns });

    return {
        reportDate: r.reportDate || '',
        source: r.source || '',
        vehicle: buildVehicle(r.vehicle),
        tiles: buildTiles(r, t),
        notices: buildNotices(r.notices),
        status: buildStatus(r.status),
        waiting: buildWaiting(r.waiting, t),
        outcome: buildOutcome(r.outcome, t),
        original: buildOriginal(r.original),
        inspection: buildInspection(r.inspection, t),
        empty: buildEmpty(r.empty),
        ai: buildAi(r.ai),
        history: {
            show: zones.length > 0 || visits.length > 0 || repairRows.length > 0,
            aiLine: (r.history && r.history.aiLine) || '',
            minor: (r.history && r.history.minor) || 0,
            car: buildHistoryCar(r, t),
            numbers: buildHistoryNumbers(zones, r.stats, t),
            zones,
            selectedZone,
            years: years.length > 1 ? years : [],
            yearsFilter: visits.length > 0,
            journey: visits.length
                ? buildJourney(visits, { open: s.open, zoneVisits, year: s.year, allVisits: s.allVisits }, t)
                : null,
            repairs,
        },
        mileage,
        equipment: buildEquipment(r.equipment, s, t),
        docinfo: buildDocInfo(r.docinfo, t),
        columns: columns
            ? {
                  hero: buildHero(r, t),
                  // a dealer-only report with no parsed data has no specs cards: they would repeat the header. With
                  // an inspection (Complete) the cards hold its fields, so they stay while the dealer part waits
                  specs: !r.inspection && (r.original || r.waiting || r.outcome) ? [] : buildSpecs(r.vehicle, t),
                  condition: buildCondition(r.condition, t),
                  // without an inspection the side column sums up the dealer documents instead
                  summary: r.inspection ? null : buildServiceSummary(r, mileage, t),
                  price: buildPrice(r.pricing),
                  chips: buildChips(r.equipment, s),
              }
            : null,
    };
};
